/**
 * useStoreCredits — the current store's client credits, for the Credits page, the account nav link
 * and the site-wide "you have credits" bar. One cached query per store, shared by all three.
 * Customers only: employees and brand users browse in preview and have no store of their own.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import creditService from "../services/creditService.js";

export const STORE_CREDITS_KEY = "storeCredits";

export const useStoreCredits = () => {
  const { store, isAuthenticated, isEmployeePreview } = useAuth();
  const clientId = store?.id;
  const active = !!(isAuthenticated && clientId && !isEmployeePreview);

  const { data, isLoading, error } = useQuery({
    queryKey: [STORE_CREDITS_KEY, clientId],
    queryFn: () => creditService.getStoreCredits(clientId),
    enabled: active,
    staleTime: 2 * 60 * 1000,
  });

  return {
    enabled: active && data?.enabled === true,
    availableTotal: data?.availableTotal ?? 0,
    credits: data?.credits ?? [],
    // The credits that can be spent today, for checkout.
    availableCredits: (data?.credits ?? []).filter((c) => c.status === "available" && c.remainingAmount > 0),
    // The house brands this store's company carries: the only products a Stash House credit covers.
    stashHouseBrands: data?.stashHouseBrands ?? [],
    storeName: store?.name,
    isLoading: active && isLoading,
    error,
  };
};

export default useStoreCredits;
